/**
 * CADMarkerDetailPanel — side drawer showing DRBFM failure-mode details for
 * a clicked CAD marker. The marker's `detailPayload` carries a `FailureMode`
 * when the marker came from `useDRBFMWithCAD`.
 *
 * Tied to the Toyota Gosei DRBFM↔3D CAD linker PoC (TG #673).
 */
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CADMarker } from '../../types/cad';
import { getSeverityColor } from '../../types/drbfm';
import type { FailureMode } from '../../types/drbfm';

interface Props {
  open: boolean;
  marker: CADMarker | null;
  onClose: () => void;
}

export const CADMarkerDetailPanel: React.FC<Props> = ({ open, marker, onClose }) => {
  const { t } = useTranslation();
  const fm = (marker?.detailPayload as FailureMode | undefined) ?? null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 380 } }}
    >
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          {t('cad.markerDetail.title')}
        </Typography>

        {!fm && (
          <Alert severity="info">{t('cad.markerDetail.noData')}</Alert>
        )}

        {fm && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={fm.severity}
                size="small"
                sx={{ bgcolor: getSeverityColor(fm.severity), color: '#fff' }}
              />
              {typeof fm.rpnScore === 'number' && (
                <Chip label={`RPN ${fm.rpnScore}`} size="small" variant="outlined" />
              )}
              {marker?.confidence !== undefined && (
                <Chip
                  label={t('cad.markerDetail.confidence', {
                    value: (marker.confidence * 100).toFixed(0),
                  })}
                  size="small"
                  variant={marker.confidence < 0.4 ? 'outlined' : 'filled'}
                  color={marker.confidence < 0.4 ? 'default' : 'primary'}
                />
              )}
              {fm.cadAnchor?.source && (
                <Chip
                  label={t(`cad.marker.source.${fm.cadAnchor.source}`)}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>

            <Typography variant="subtitle1">{fm.failureDescription}</Typography>

            <Divider />

            <Section title={t('cad.markerDetail.cause')}>{fm.failureCause || '—'}</Section>
            <Section title={t('cad.markerDetail.effect')}>{fm.failureEffect || '—'}</Section>

            {fm.cadAnchor?.partLabel && (
              <Section title={t('cad.markerDetail.part')}>{fm.cadAnchor.partLabel}</Section>
            )}

            {fm.cadAnchor?.reasoning && (
              <Section title={t('cad.markerDetail.locationEvidence')}>{fm.cadAnchor.reasoning}</Section>
            )}

            {fm.knowledgeReferences?.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">
                  {t('cad.markerDetail.relatedDocuments')}
                </Typography>
                <Stack spacing={0.5}>
                  {fm.knowledgeReferences.map((k) => (
                    <Link
                      key={k.documentId}
                      href={`/documents/${k.documentId}`}
                      underline="hover"
                    >
                      {k.title}
                    </Link>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="body2">{children}</Typography>
  </Box>
);
